// Floodlights on the grandstand roof: a lamp bank on a short mast at every other column, aimed
// down at the track. They light up as dusk falls, with the floodlight in lighting.ts (FLOOD_DIR).
import {
  AdditiveBlending, BoxGeometry, Color, CylinderGeometry, Group, IcosahedronGeometry, InstancedMesh, Matrix4,
  MeshBasicMaterial, MeshStandardMaterial, PlaneGeometry, Quaternion, Vector3,
} from 'three';
import { STAND, standModulesX } from './layout';

const MAST = 2.1;
const TILT = 0.75; // radians the lamp face looks down from horizontal

export class Floodlights {
  readonly group = new Group();
  private lens: MeshBasicMaterial;
  private halo: MeshBasicMaterial;
  private halos: InstancedMesh;

  constructor() {
    this.group.name = 'floodlights';
    const spots: Vector3[] = [];
    for (const x0 of standModulesX()) for (const off of [0, 12]) spots.push(new Vector3(x0 + off, STAND.roofFrontY, STAND.frontZ + 1.0));
    const n = spots.length;
    const metal = new MeshStandardMaterial({ color: '#3a3d42', roughness: 0.45, metalness: 0.6 });
    const masts = new InstancedMesh(new CylinderGeometry(0.06, 0.08, MAST, 6).translate(0, MAST / 2, 0), metal, n);
    const heads = new InstancedMesh(new BoxGeometry(1.5, 0.8, 0.3), metal, n);
    this.lens = new MeshBasicMaterial({ color: new Color(0.35, 0.36, 0.38) });
    const lenses = new InstancedMesh(new PlaneGeometry(1.36, 0.66), this.lens, n);
    this.halo = new MeshBasicMaterial({ color: '#dfe8ff', transparent: true, opacity: 0, blending: AdditiveBlending, depthWrite: false });
    this.halos = new InstancedMesh(new IcosahedronGeometry(1.0, 1), this.halo, n);
    const m = new Matrix4(), q = new Quaternion(), one = new Vector3(1, 1, 1), p = new Vector3(), off = new Vector3();
    // The face looks towards the track (+z) and down.
    q.setFromAxisAngle(new Vector3(1, 0, 0), TILT);
    spots.forEach((s, i) => {
      masts.setMatrixAt(i, m.compose(s, new Quaternion(), one));
      const head = p.copy(s).add(new Vector3(0, MAST + 0.3, 0));
      heads.setMatrixAt(i, m.compose(head, q, one));
      off.set(0, 0, 0.16).applyQuaternion(q);
      lenses.setMatrixAt(i, m.compose(head.clone().add(off), q, one));
      off.set(0, 0, 0.5).applyQuaternion(q);
      this.halos.setMatrixAt(i, m.compose(head.clone().add(off), q, new Vector3(1.1, 0.7, 0.4)));
    });
    // Like the stand they sit on, the lamps cast no shadows (and never into their own beams).
    for (const im of [masts, heads]) im.receiveShadow = true;
    for (const im of [masts, heads, lenses, this.halos]) { im.frustumCulled = false; this.group.add(im); }
  }

  /** Brightness of the lamps, 0 (off, by day) to 1 (full night). */
  set(on: number): void {
    this.lens.color.setRGB(0.35, 0.36, 0.38).lerp(new Color(7, 7.4, 8), on);
    this.halo.opacity = 0.14 * on;
    this.halos.visible = on > 0.01;
  }
}
